"use client";

import * as React from "react";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PortableTextBlock } from "sanity";
import Image from "next/image";
import { GROQButton, GROQImage } from "@/sanity/lib/definitions";
import { motion } from "framer-motion";
import ChipStatue from "@/components/global/ui/ChipStatue";

import archerChip from "@/public/global/archerChip.png";
import { CTAButton } from "@/shadcn/ui/button";

export default function StepsScroll({
  title,
  items,
  cta,
  className,
  futures = false,
}: {
  title: PortableTextBlock[];
  items: {
    title: PortableTextBlock[];
    image: GROQImage;
    text: PortableTextBlock[];
  }[];
  cta?: GROQButton;
  className?: string;
  futures?: boolean;
}) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const stickyContentRef = React.useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [containerHeight, setContainerHeight] = React.useState<number | null>(
    null,
  );
  const [stickyContentHeight, setStickyContentHeight] = React.useState<
    number | null
  >(null);
  const [isLargeScreen, setIsLargeScreen] = React.useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 993;
      setIsLargeScreen(isLg);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    const calcActive = () => {
      if (
        !isLargeScreen ||
        !containerRef.current ||
        !items ||
        items.length === 0 ||
        stickyContentHeight === null
      )
        return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const scrolledIntoContainer = -containerRect.top;
      const remInPixels = parseFloat(
        getComputedStyle(document.documentElement).fontSize,
      );
      const stickyContentHeightPx = stickyContentHeight * remInPixels;
      const stickyTopOffsetPx = 10 * remInPixels; // The sticky position is at top-[10rem]
      const itemsScrollPosition = scrolledIntoContainer - stickyTopOffsetPx;
      const itemHeightPx = 432;
      const gapHeightPx = 130;
      const itemWithGapPx = itemHeightPx + gapHeightPx;
      const totalItemsScrollSpace = items.length * itemWithGapPx;
      const scrollProgress = Math.min(
        itemsScrollPosition / totalItemsScrollSpace,
        1,
      );
      let newIndex = Math.floor(scrollProgress * items.length);

      newIndex = Math.max(0, Math.min(items.length - 1, newIndex));

      if (newIndex !== activeIndex) {
        setActiveIndex(newIndex);
      }
    };

    const onScroll = () => requestAnimationFrame(calcActive);
    calcActive();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [activeIndex, items, stickyContentHeight, isLargeScreen]);

  React.useEffect(() => {
    const calculateHeight = () => {
      if (
        !isLargeScreen ||
        !stickyContentRef.current ||
        !items ||
        items.length === 0
      )
        return;

      const stickyContentHeightPx = stickyContentRef.current.offsetHeight;

      if (stickyContentHeightPx > 0) {
        const remInPixels = parseFloat(
          getComputedStyle(document.documentElement).fontSize,
        );
        const stickyContentHeightRem = stickyContentHeightPx / remInPixels;
        const itemHeightRem = 432 / 8;
        const gapHeightRem = 130 / 8;
        const bufferRem = itemHeightRem + gapHeightRem;
        const totalHeight =
          stickyContentHeightRem +
          items.length * itemHeightRem +
          (items.length - 1) * gapHeightRem +
          bufferRem;

        setStickyContentHeight(stickyContentHeightRem);
        setContainerHeight(totalHeight);
      }
    };

    const timeout1 = setTimeout(calculateHeight, 150);
    const timeout2 = setTimeout(calculateHeight, 400);
    const timeout3 = setTimeout(calculateHeight, 800);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateHeight, 100);
      });
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateHeight, 200);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, [items, isLargeScreen]);

  if (!items || items.length === 0) return null;

  return (
    <section className={cn("relative py-[12rem]", className)}>
      <div
        ref={containerRef}
        className="container relative"
        style={{
          ...(isLargeScreen &&
            containerHeight !== null && { height: `${containerHeight}rem` }),
        }}
      >
        <div
          ref={stickyContentRef}
          className="flex flex-col items-center lg:sticky lg:top-[10rem]"
        >
          <div className="flex flex-col items-center">
            <ChipStatue className="mb-[2.5rem]" image={archerChip} />

            {title && (
              <div className="richtext mb-[6rem] lg:mb-[15rem]">
                <PortableText value={title} components={richTextComponent} />
              </div>
            )}
          </div>

          {items && (
            <div className="w-[min(185.125rem,100%)] flex flex-col gap-[10rem] lg:gap-[1rem] relative">
              {items.map((item, i) => (
                <div
                  key={i}
                  className={cn(
                    "flex flex-col lg:flex-row items-center lg:justify-between gap-[1.75rem] w-full",
                    i === 0 ? "relative" : "lg:absolute lg:top-0 lg:left-0",
                  )}
                >
                  {/* left side text */}
                  <div className="w-full lg:w-[55.125rem]">
                    <motion.div
                      initial={
                        isLargeScreen ? { y: "100%", opacity: 0 } : false
                      }
                      animate={
                        isLargeScreen
                          ? i === activeIndex
                            ? { y: 0, opacity: 1 }
                            : i === activeIndex - 1
                              ? { y: "-100%", opacity: 0.2 }
                              : i < activeIndex
                                ? { y: "-100%", opacity: 0 }
                                : { y: "100%", opacity: 0 }
                          : { y: 0, opacity: 1 }
                      }
                      transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 18,
                      }}
                      className="flex justify-center items-center lg:flex-col lg:items-start gap-[2rem] lg:h-[35rem]"
                    >
                      <div className={cn("flex justify-center items-center w-[7rem] h-[7rem] rounded-[1.5rem]", futures ? "bg-[#101337]" : "bg-[#051E22]")}>
                        <p className={cn("mb-0 font-bold leading-none font-instrument-sans text-[3.5rem]", futures ? "text-[#D1D5FF]" : "text-[#85D0A6]")}>
                          0{i + 1}
                        </p>
                      </div>

                      {item?.title && (
                        <div className="richtext richtext-start">
                          <PortableText
                            value={item.title}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </motion.div>
                  </div>

                  {/* middle image */}
                  <div className="relative w-full aspect-square overflow-clip rounded-[3rem] lg:w-[54rem] lg:h-[54rem]">
                    {item?.image && (
                      <motion.div
                        initial={
                          isLargeScreen ? { y: "calc(-100% - 1px)" } : false
                        }
                        animate={
                          isLargeScreen
                            ? i === activeIndex
                              ? { y: 0 }
                              : i < activeIndex
                                ? { y: "calc(100% + 1px)" }
                                : { y: "calc(-100% - 1px)" }
                            : { y: 0 }
                        }
                        transition={{
                          type: "spring",
                          stiffness: 120,
                          damping: 18,
                        }}
                      >
                        <Image
                          className="!w-full !h-full object-cover"
                          src={item.image}
                          alt=""
                        />
                      </motion.div>
                    )}
                  </div>

                  {/* right side text */}
                  <div className="w-full lg:w-[55.125rem]">
                    <motion.div
                      initial={
                        isLargeScreen ? { y: "100%", opacity: 0 } : false
                      }
                      animate={
                        isLargeScreen
                          ? i === activeIndex
                            ? { y: 0, opacity: 1 }
                            : i === activeIndex - 1
                              ? { y: "-100%", opacity: 0.2 }
                              : i < activeIndex
                                ? { y: "-100%", opacity: 0 }
                                : { y: "100%", opacity: 0 }
                          : { y: 0, opacity: 1 }
                      }
                      transition={{
                        type: "spring",
                        stiffness: 120,
                        damping: 18,
                      }}
                      className="flex flex-col justify-center lg:h-[35rem]"
                    >
                      {item?.text && (
                        <div className="richtext lg:richtext-start prose-headings:text-[#202223] prose-headings:text-[7rem] prose-p:w-[min(51.875rem,100%)]">
                          <PortableText
                            value={item.text}
                            components={richTextComponent}
                          />
                        </div>
                      )}
                    </motion.div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {cta && (
        <div className="flex justify-center px-[2rem] lg:px-0 pt-[5rem] lg:pt-[9.55rem]">
          <CTAButton data={cta} />
        </div>
      )}
    </section>
  );
}
